/* eslint-disable react/react-in-jsx-scope */
import Polygon from "@/assets/icones/polygon.svg?react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import { useMutateData } from "@/hooks/useMutation";
import { convertArea, convertLength } from "@/libs/utils";
import { ParcelLocationType } from "@/types/dashboard";
import { zodResolver } from "@hookform/resolvers/zod";
import * as turf from "@turf/turf";
import { HslColor } from "@uiw/react-color";
import L from "leaflet";
import {
  Check,
  ChevronDown,
  Edit3Icon,
  PaintBucket,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { hexToHsl, hslToHex, listColors, parseHslString } from "../utils/utils";
import { ColorCustomPicker } from "./ColorCustomPicker";

const formSchema = z.object({
  name: z.string().min(1, { message: "Le nom est requis" }).max(100),
  description: z.string().max(500).optional(),
});

const CardInfos = ({
  map,
  status,
  setStatus,
  parcel,
  setParcel,
  isReadOnly,
  idProject,
  selectedPolygon,
  setSelectedPolygon,
  folders,
}: {
  map: L.Map | null;
  status: string;
  setStatus: (status: "close" | "add" | "read" | "edit" | "delete") => void;
  parcel: ParcelLocationType | null;
  setParcel: (parcel: ParcelLocationType | null) => void;
  isReadOnly: boolean;
  idProject: number;
  selectedPolygon: L.Polygon | null;
  setSelectedPolygon: (polygon: L.Polygon | null) => void;
  folders: any;
}) => {
  return (
    <div className="bg-white rounded-md p-3 flex flex-col space-y-6 w-64">
      {status === "read" ? (
        <ReadInfos
          parcel={parcel}
          setStatus={setStatus}
          isReadOnly={isReadOnly}
          projectId={idProject}
          selectedPolygon={selectedPolygon}
        />
      ) : (
        <SaveInfos
          parcel={parcel}
          setParcel={setParcel}
          setStatus={setStatus}
          idProject={idProject}
          selectedPolygon={selectedPolygon}
          status={status}
          setSelectedPolygon={setSelectedPolygon}
          folders={folders}
        />
      )}
    </div>
  );
};

const SaveInfos = ({
  parcel,
  setParcel,
  setStatus,
  idProject,
  selectedPolygon,
  status,
  setSelectedPolygon,
  folders,
}: {
  parcel: ParcelLocationType | null;
  setParcel: (parcel: ParcelLocationType | null) => void;
  setStatus: (status: "close" | "add" | "read" | "edit" | "delete") => void;
  idProject: number;
  selectedPolygon: L.Polygon | null;
  status: string;
  setSelectedPolygon: (polygon: L.Polygon | null) => void;
  folders: any;
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingParcel, setLoadingParcel] = useState(true);
  const [fillColor, setFillColor] = useState(parcel?.fillColor || "");
  const [strokeColor, setStrokeColor] = useState(parcel?.strokeColor || "");
  const [opacity, setOpacity] = useState(parcel?.opacity ?? 0.4);
  const [strokeWidth, setStrokeWidth] = useState(parcel?.strokeWidth ?? 1);
  const [folder, setFolder] = useState(parcel?.folder ?? 0);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: parcel?.name || "",
      description: parcel?.description || "",
    },
  });

  const { mutate: updatePolygon } = useMutateData({
    method: "POST",
    id: `parcels-${idProject}`,
    onSuccess: (data) => {
      // Mettre à jour la géométrie Leaflet avec nouvelles coordonnées (déjà correct si edit)

      // selectedPolygon?.setLatLngs(
      //   data.coordinates.map((c) => L.latLng(c[0], c[1]))
      // );

      // Mettre à jour le style au cas où
      // selectedPolygon?.setStyle({
      //   fillColor: data?.fillColor,
      //   color: data?.strokeColor,
      //   opacity: data?.opacity,
      //   weight: data?.strokeWidth,
      // });
      // Propager l'update au parent React
      setParcel({
        ...parcel,
        name: data.name,
        description: data.description,
        fillColor: data.fillColor,
        strokeColor: data.strokeColor,
        strokeWidth: data.strokeWidth,
        opacity: data.opacity,
        folder: data.folder ? data.folder?.id : 0,
      });
      setSelectedPolygon(selectedPolygon);

      setLoading(false);
      // setStatus("read");

      toast({ title: "✅ Polygone mis à jour avec succès !" });
      console.log("✅ Polygone mis à jour avec succès :", data);
    },
  });

  useEffect(() => {
    if (!selectedPolygon) return;

    selectedPolygon.setStyle({
      fillColor,
      color: strokeColor,
      fillOpacity: opacity,
      weight: strokeWidth,
    });
  }, [fillColor, strokeColor, opacity, strokeWidth, selectedPolygon]);
  // Synchronisation édition Leaflet selon status
  useEffect(() => {
    if (!selectedPolygon) return;

    if (status === "edit") {
      selectedPolygon.editing.enable();
    } else {
      selectedPolygon.editing.disable();
    }

    return () => {
      selectedPolygon.editing.disable();
    };
  }, [selectedPolygon, status]);

  useEffect(() => {
    if (parcel) {
      setLoadingParcel(true);
      const timeout = setTimeout(() => {
        form.reset({
          name: parcel?.name,
          description: parcel?.description,
        });
        setFillColor(parcel.fillColor);
        setStrokeColor(parcel.strokeColor);
        setOpacity(parcel.opacity);
        setStrokeWidth(parcel.strokeWidth);
        setFolder(parcel.folder);
        setLoadingParcel(false);
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [parcel]);

  const handleSaveData = async (data: z.infer<typeof formSchema>) => {
    if (!parcel && !selectedPolygon) return;

    setLoading(true);
    // Récupérer les nouvelles coordonnées depuis Leaflet
    const newCoords = (selectedPolygon.getLatLngs()[0] as L.LatLng[]).map(
      (latlng) => [latlng.lat, latlng.lng]
    );

    const { id, ...rest } = parcel;
    const parcelData = rest;
    if (parcelData?.codeArr) {
      parcelData.feuille = parseInt(parcelData.feuille);
      parcelData.contenance = parseInt(parcelData.contenance);
    }
    updatePolygon({
      endpoint: `${import.meta.env.VITE_API_ENDPOINT}/location/save`,
      body: {
        ...rest,
        name: data.name,
        description: data.description,
        folder,
        fillColor,
        strokeColor,
        opacity,
        strokeWidth,
        coordinates: newCoords,
        type: parcelData?.codeArr ? 1 : 2,
        toEdit: parseInt(parcel?.id.split("_")[1]),
      },
    });
    setStatus("close");
    setLoading(false);
  };

  const initialFillColor: HslColor =
    parcel && parcel.fillColor
      ? (() => {
          const hsl = hexToHsl(parcel.fillColor as string);
          if (hsl) {
            return hsl;
          }
          return { h: 0, s: 0, l: 50 }; // fallback
        })()
      : { h: 210, s: 100, l: 50 };
  const initialStrokeColor: HslColor =
    parcel && parcel.strokeColor
      ? (() => {
          const hsl = hexToHsl(parcel.strokeColor as string);
          if (hsl) {
            return hsl;
          }
          return { h: 0, s: 0, l: 50 }; // fallback
        })()
      : { h: 210, s: 100, l: 50 };

  const onColorChange = (colorStr: string, type: "fill" | "stroke") => {
    const color = parseHslString(colorStr);
    if (!color) {
      console.error("HSL invalide:", colorStr);
      return;
    }
    if (type === "fill") {
      setFillColor(hslToHex(color));
    } else {
      setStrokeColor(hslToHex(color));
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSaveData)}
        className="space-y-4 scroll-auto"
      >
        <div className="flex items-center justify-between">
          {loadingParcel ? (
            <div className="flex items-center gap-3 w-full">
              <Polygon className="size-6" />
              <Skeleton className="rounded-md flex-1 h-10  bg-[#ecedee]" />
            </div>
          ) : (
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="flex items-center gap-3">
                  <FormLabel>
                    <Polygon className="size-6" />
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Nom" {...field} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <div className="flex items-center gap-2 ml-3">
            <button
              type="button"
              onClick={() => setStatus("close")}
              aria-label="Fermer"
            >
              <X className="size-6" />
            </button>
          </div>
        </div>
        {loadingParcel ? (
          <div className=" space-y-3 w-full">
            <FormLabel>Description</FormLabel>
            <Skeleton className="rounded-md flex-1 h-16  bg-[#ecedee]" />
          </div>
        ) : (
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Description"
                    {...field}
                    autoComplete="off"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        {/* Styles */}
        <div className="flex justify-between items-center">
          <p>Contour</p>
          <div className="flex items-center gap-2">
            <select
              value={strokeWidth}
              onChange={(e) => setStrokeWidth(Number(e.target.value))}
              className="border px-3 py-2 rounded-md"
            >
              {[0.5, 1, 2, 3, 4, 8, 12, 24].map((val) => (
                <option key={val} value={val}>
                  {val} px
                </option>
              ))}
            </select>
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" aria-label="Choisir couleur contour">
                  <PaintBucket />
                  <div
                    className="w-6 h-2 mt-1 border"
                    style={{ backgroundColor: strokeColor || "#fff" }}
                  />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72">
                <div className="flex flex-wrap gap-3">
                  {listColors.map((color) => (
                    <div
                      key={color}
                      className={`size-6 rounded-full cursor-pointer border relative flex items-center justify-center group ${
                        strokeColor === color ? "ring-1" : ""
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setStrokeColor(color)}
                    >
                      <Check
                        className={`size-4 text-white ${
                          strokeColor === color ? "opacity-100" : "opacity-0"
                        }`}
                        style={{ filter: "drop-shadow(0 0 2px #514e4e)" }}
                      />
                    </div>
                  ))}
                </div>
                <div className="justify-end flex mt-3">
                  <ColorCustomPicker
                    color={initialStrokeColor}
                    onColorChange={(color) => onColorChange(color, "stroke")}
                  />
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <div className="flex justify-between items-center">
          <p>Remplissage</p>
          <div className="flex items-center gap-2">
            <select
              value={opacity}
              onChange={(e) => setOpacity(Number(e.target.value))}
              className="border px-3 py-2 rounded-md"
            >
              {[1, 0.75, 0.5, 0.25, 0.15, 0].map((val) => (
                <option key={val} value={val}>
                  {val * 100}%
                </option>
              ))}
            </select>
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" aria-label="Choisir couleur remplissage">
                  <PaintBucket />
                  <div
                    className="w-6 h-2 mt-1 border"
                    style={{ backgroundColor: fillColor || "#fff" }}
                  />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72">
                <div className="flex flex-wrap gap-3">
                  {listColors.map((color) => (
                    <div
                      key={color}
                      className={`size-6 rounded-full cursor-pointer border relative flex items-center justify-center group ${
                        fillColor === color ? "ring-1" : ""
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setFillColor(color)}
                    >
                      <Check
                        className={`size-4 text-white ${
                          fillColor === color ? "opacity-100" : "opacity-0"
                        }`}
                        style={{ filter: "drop-shadow(0 0 2px #514e4e)" }}
                      />
                    </div>
                  ))}
                </div>
                <div className="justify-end flex mt-3">
                  <ColorCustomPicker
                    color={initialFillColor}
                    onColorChange={(color) => onColorChange(color, "fill")}
                  />
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        {/* <div className="flex justify-between items-center">
          <p className="m-0">Dossier</p>
          <div className="flex items-center gap-2">
            <select
              value={folder}
              onChange={(e) => setFolder(Number(e.target.value))}
              className="border px-3 py-2 rounded-md"
            >
              {folders &&
                folders.map((val) =>
                  val.id === 0 ? (
                    <option key={val.id} value={val.id}>
                      Sans dossier
                    </option>
                  ) : (
                    <option key={val.id} value={val.id}>
                      {val.name}
                    </option>
                  )
                )}
            </select>
          </div>
        </div> */}
        <div className="flex justify-start">
          <Button type="submit" variant="outline">
            <Edit3Icon size={18} className="mr-3" /> Sauvegarder
          </Button>
        </div>
      </form>
    </Form>
  );
};

const ReadInfos = ({
  parcel,
  setStatus,
  isReadOnly,
  projectId,
  selectedPolygon,
}: {
  parcel: ParcelLocationType | null;
  setStatus: (status: "close" | "add" | "read" | "edit" | "delete") => void;
  isReadOnly: boolean;
  projectId: number | null;
  selectedPolygon: any;
}) => {
  const { mutateAsync: deleteOne } = useMutateData({
    method: "POST",
    id: `parcels-${projectId}`,
  });
  const [loadingParcel, setLoadingParcel] = useState(true);
  const [unityPeri, setUnityPeri] = useState("m");
  const [unityAire, setUnityAire] = useState("Ha");
  const { perimeter, area } = useMemo(() => {
    const coords = parcel?.coordinates || [];

    if (coords.length < 3) return { perimeter: 0, area: 0 };

    const lngLatCoords = coords.map(([lat, lng]) => [lng, lat]);

    const closed =
      lngLatCoords[0][0] === lngLatCoords.at(-1)?.[0] &&
      lngLatCoords[0][1] === lngLatCoords.at(-1)?.[1];

    const closedCoords = closed
      ? lngLatCoords
      : [...lngLatCoords, lngLatCoords[0]];

    const turfPolygon = turf.polygon([closedCoords]);
    const rawPerimeter = turf.length(turf.lineString(closedCoords), {
      units: "meters",
    });
    const rawArea = turf.area(turfPolygon);

    return {
      perimeter: convertLength(rawPerimeter, unityPeri, 2),
      area: convertArea(rawArea, unityAire, 2),
    };
  }, [parcel?.coordinates, unityAire, unityPeri]);

  const handleDeleted = async (idParcel: string, idProject: number | null) => {
    const urlBase = import.meta.env.VITE_API_ENDPOINT;
    const isLocation = idProject === null;
    const idparcel = Number(idParcel.split("_")[1]);
    await deleteOne({
      endpoint: `${urlBase}/${
        isLocation ? "location" : "parcel"
      }/delete-multiple`,
      body: {
        ids: [idparcel],
        project: idProject,
      },
    });
    setStatus("close");
    toast({ title: "✅ Polygone supprimé avec succès !" });
  };
  useEffect(() => {
    if (parcel) {
      setLoadingParcel(true);
      const timeout = setTimeout(() => {
        setLoadingParcel(false);
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [parcel]);
  return (
    <div className="flex flex-col space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Polygon className="size-6" />
          {loadingParcel ? (
            <Skeleton className="rounded-md w-28 h-10  bg-[#ecedee]" />
          ) : (
            <p className="text-sm font-semibold m-0">{parcel?.name}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {selectedPolygon && !isReadOnly && (
            <button
              className="size-8 flex items-center justify-center hover:bg-muted rounded-md"
              onClick={() =>
                handleDeleted(parcel?.id, parcel?.nomCom ? projectId : null)
              }
            >
              <Trash2 className="size-5" />
            </button>
          )}
          <button
            onClick={() => setStatus("close")}
            className="group size-8 flex items-center justify-center hover:bg-red-300 rounded-md"
          >
            <X className="size-6" />
          </button>
        </div>
      </div>
      {loadingParcel && parcel?.description ? (
        <Skeleton className="rounded-md w-full h-16  bg-[#ecedee]" />
      ) : (
        parcel?.description && <p className="m-0">{parcel?.description}</p>
      )}
      <div className="flex justify-between items-center">
        <div>
          {loadingParcel ? (
            <>
              <Skeleton className="rounded-md w-16 mb-2 h-4 bg-[#ecedee]" />
              <Skeleton className="rounded-md w-full h-4 bg-[#ecedee]" />
            </>
          ) : (
            <>
              <p className="m-0">Périmètre</p>
              <p className="m-0">
                {perimeter}{" "}
                {unityPeri === "nautical_mile" ? "Miles nautiques" : unityPeri}
              </p>
            </>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild className="cursor-pointer">
            <button className="size-9 flex items-center justify-center">
              <ChevronDown size={16} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="z-[9999999999999999]">
            <DropdownMenuGroup>
              <DropdownMenuItem
                className="cursor-pointer hover:bg-white focus:bg-white focus:text-black"
                onClick={(e) => {
                  e.stopPropagation();
                  setUnityPeri("m");
                }}
              >
                M
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer hover:bg-white focus:bg-white focus:text-black"
                onClick={(e) => {
                  e.stopPropagation();
                  setUnityPeri("cm");
                }}
              >
                Cm
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer hover:bg-white focus:bg-white focus:text-black"
                onClick={(e) => {
                  e.stopPropagation();
                  setUnityPeri("km");
                }}
              >
                Km
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer hover:bg-white focus:bg-white focus:text-black"
                onClick={(e) => {
                  e.stopPropagation();
                  setUnityPeri("nautical_mile");
                }}
              >
                Miles nautiques
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="flex justify-between items-center">
        <div>
          {loadingParcel ? (
            <>
              <Skeleton className="rounded-md w-14 mb-2 h-4 bg-[#ecedee]" />
              <Skeleton className="rounded-md w-full h-4 bg-[#ecedee]" />
            </>
          ) : (
            <>
              <p className="m-0">Aire</p>
              <p className="m-0">
                {area}{" "}
                {unityAire === "square_nautical_miles"
                  ? "Miles nautiques carrés"
                  : unityAire}
              </p>
            </>
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild className="cursor-pointer">
            <button className="size-9 flex items-center justify-center">
              <ChevronDown size={16} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="z-[9999999999999999]">
            <DropdownMenuGroup>
              <DropdownMenuItem
                className="cursor-pointer hover:bg-white focus:bg-white focus:text-black"
                onClick={(e) => {
                  e.stopPropagation();
                  setUnityAire("ha");
                }}
              >
                Ha
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer hover:bg-white focus:bg-white focus:text-black"
                onClick={(e) => {
                  e.stopPropagation();
                  setUnityAire("m2");
                }}
              >
                M²
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer hover:bg-white focus:bg-white focus:text-black"
                onClick={(e) => {
                  e.stopPropagation();
                  setUnityAire("km2");
                }}
              >
                Km²
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer hover:bg-white focus:bg-white focus:text-black"
                onClick={(e) => {
                  e.stopPropagation();
                  setUnityAire("square_nautical_miles");
                }}
              >
                Miles nautiques carrés
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {loadingParcel ? (
        <Skeleton className="rounded-md w-32 mb-2 h-10 bg-[#ecedee]" />
      ) : selectedPolygon && !isReadOnly ? (
        <div className="flex justify-start">
          <Button variant="outline" onClick={() => setStatus("edit")}>
            <Edit3Icon size={18} className="mr-3" /> Modifier
          </Button>
        </div>
      ) : (
        !isReadOnly &&
        !selectedPolygon && (
          <div className="flex justify-start">
            ℹ️ Cliquez sur le polygone dans la carte pour le modifier.
          </div>
        )
      )}
    </div>
  );
};

export default CardInfos;
